import React from "react";
import { Tabs } from "@shared/ui/tabs";
import { PremiseTable } from "./premise";
import { MetersTable } from "./meters";
import { Info } from "lucide-react";
import { QuickStatsCard } from "@features/cx/shared/components/QuickStatsCard";
import { useMeterSummary } from "@features/mx/routeManagement/hooks";

const MeterSummary = ({ selectedPremises }: { selectedPremises: string[] }) => {
  const { data: meterSummaryData } = useMeterSummary(selectedPremises);

  const summary = meterSummaryData?.result;
  if (!summary) return null;

  return (
    <div className="border border-gray-200 rounded-lg p-4 mb-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-semibold text-lg">Meter Summary</h2>
        <Info className="h-4 w-4 text-gray-500" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* System-wide Meters */}
        <QuickStatsCard
          title="System-wide Meters"
          displayValue={`Total ${summary?.systemWideMeters?.total}`}
          trendSummary={`Assigned: ${summary?.systemWideMeters?.assigned} • Unassigned: ${summary?.systemWideMeters?.unassigned}`}
          tooltip="Total meters in the system, including assigned and unassigned units."
        />

        {/* Meters in Selected Premises */}
        <QuickStatsCard
          title="Meters in Selected Premises"
          displayValue={`Total ${summary?.premiseMeters?.total} `}
          trendSummary={`Active: ${summary?.premiseMeters?.active} • Inactive: ${summary?.premiseMeters?.inactive}`}
          tooltip="Meters currently associated with the selected premises."
        />

        {/* Meter Condition */}
        <QuickStatsCard
          title="Meter Condition"
          displayValue={`Total ${summary?.meterConditions?.normal + summary?.meterConditions?.faulty + summary?.meterConditions?.recent}`}
          trendSummary={`Normal: ${summary?.meterConditions?.normal} • Faulty: ${summary?.meterConditions?.faulty} • RCNT: ${summary?.meterConditions?.recent}`}
          tooltip="Breakdown of meter health across statuses."
        />
      </div>
    </div>
  );
};

interface LandingProps {
  selectedPremises: string[];
  setSelectedPremises: React.Dispatch<React.SetStateAction<string[]>>;
}

const Landing = ({ selectedPremises, setSelectedPremises }: LandingProps) => {
  const tabComponents = {
    premises: {
      label: "Premises",
      shortLabel: "premises",
      component: (
        <PremiseTable
          selectedPremises={selectedPremises}
          setSelectedPremises={setSelectedPremises}
        />
      ),
    },
    meters: {
      label: "Meters",
      shortLabel: "meters",
      component: <MetersTable selectedPremises={selectedPremises} />,
    },
  };

  const urlMapping = {
    premises: "premises",
    meters: "meters",
  };

  return (
    <div>
      {/* Only show Meter Summary when at least one premise is selected */}
      {selectedPremises.length > 0 && (
        <MeterSummary selectedPremises={selectedPremises} />
      )}

      {/* Tabs Section */}
      <div className="border border-gray-200 p-4 rounded-lg">
        <Tabs
          defaultValue="premises"
          urlMapping={urlMapping}
          tabComponents={tabComponents}
          tabsListFullWidth={true}
          tabsListClassName="grid grid-cols-2"
        />
      </div>
    </div>
  );
};

export default Landing;
